// ============================================================
// Mock Public School Website Data
// ============================================================
// Production-quality mock fallback dataset for Hera Public School.
// Used when API values are missing/null or API is unavailable.

export const mockSchoolWebsiteData = {
  school_name: 'Hera Public School',
  tagline: 'Empowering Young Minds for a Brighter Future',
  logo: '/shreeji-logo-clean.png',
  favicon: '/favicon.ico',
  primary_color: '#1e3a8a', // Deep Blue
  secondary_color: '#0284c7', // Sky Accent
  login_image: '/images/school_bg.png',
  website_enabled: true,
  erp_enabled: true,
  template: 'default',

  // Hero Section
  hero_title: 'Empowering Young Minds for a Brighter Future',
  hero_subtitle: 'Building knowledge, character and confidence through quality education.',
  hero_image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1600&q=80',
  hero_cta_primary: 'Apply for Admission',
  hero_cta_secondary: 'Explore Campus',

  // Quick Info
  quick_info: {
    established: '2001',
    affiliation: 'CBSE',
    classes: 'Nursery to Grade 12',
    medium: 'English',
    school_type: 'Co-Educational',
  },

  // About School
  about_title: 'About Hera Public School',
  about_content:
    'Hera Public School is committed to providing a nurturing and inspiring learning environment where students develop strong academic foundations, confidence, creativity and character. Our approach combines quality education with sports, technology, arts and co-curricular activities to help every student discover their potential.',
  about_image:
    'https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=1200&q=80',

  // Principal Message
  principal_name: 'Dr. Ananya Sharma',
  principal_title: 'Principal, Hera Public School',
  principal_message:
    'Welcome to Hera Public School. Our goal is to create an environment where every student feels supported, inspired and prepared for a successful future. We believe education is not only about academic achievement but also about developing responsible, confident and compassionate individuals.',
  principal_image:
    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80',

  // School Highlights
  school_highlights: [
    {
      title: 'State-of-the-Art STEM Labs',
      description: 'Advanced robotics, physics, chemistry, and biology labs designed for practical experimentation.',
      icon: 'Atom',
    },
    {
      title: 'Holistic Development & Sports',
      description: 'Comprehensive sports facilities including basketball, football, swimming, and indoor games.',
      icon: 'Trophy',
    },
    {
      title: 'Safe & Green 10-Acre Campus',
      description: 'CCTV monitored campus with lush greenery, dedicated play zones, and modern safety protocols.',
      icon: 'ShieldCheck',
    },
    {
      title: 'Global Mentorship Programs',
      description: 'Interactive workshops, career counselling, and international exchange opportunities.',
      icon: 'Globe',
    },
  ],

  // Academics
  academics: [
    {
      id: 'primary',
      title: 'Primary Education',
      grades: 'Nursery to Grade 5',
      description:
        'Focuses on foundational literacy, numeracy, experiential inquiry, and creative expression in an encouraging environment.',
      features: ['Activity-based Learning', 'Language Development', 'Basic STEM Concepts', 'Arts & Crafts'],
    },
    {
      id: 'middle',
      title: 'Middle School',
      grades: 'Grade 6 to Grade 8',
      description:
        'Encourages critical thinking, collaborative projects, analytical reasoning, and structured co-curricular participation.',
      features: ['Integrated Science & Math', 'Digital Literacy', 'Foreign Language Options', 'Sports Integration'],
    },
    {
      id: 'secondary',
      title: 'Secondary Education',
      grades: 'Grade 9 & Grade 10',
      description:
        'Comprehensive CBSE curriculum aimed at mastering core subjects, board exam preparation, and conceptual depth.',
      features: ['CBSE Board Alignment', 'Mock Examinations', 'Career Counselling', 'Skill Workshops'],
    },
    {
      id: 'senior-secondary',
      title: 'Senior Secondary Education',
      grades: 'Grade 11 & Grade 12',
      description:
        'Specialized academic streams (Science, Commerce, Humanities) equipped with JEE/NEET/CUET competitive exam guidance.',
      features: ['Science / Commerce / Arts', 'Competitive Exam Support', 'Advanced Laboratory Work', 'Leadership Training'],
    },
  ],

  // Facilities
  facilities: [
    { name: 'Smart Classrooms', description: 'Interactive digital boards and multimedia learning tools in every room.', icon: 'Monitor' },
    { name: 'Science Laboratories', description: 'Fully equipped Physics, Chemistry, and Biology practical labs.', icon: 'FlaskConical' },
    { name: 'Computer Lab', description: 'High-speed internet enabled computer stations with modern software.', icon: 'Laptop' },
    { name: 'Library', description: 'Extensive collection of over 15,000 books, journals, and digital resources.', icon: 'BookOpen' },
    { name: 'Sports Ground', description: 'Dedicated football field, basketball court, cricket pitch, and athletics track.', icon: 'Activity' },
    { name: 'Transportation', description: 'GPS-enabled fleet of buses with trained drivers and bus attendants.', icon: 'Bus' },
    { name: 'Safe & Secure Campus', description: '24/7 Security personnel, biometric gates, and complete CCTV coverage.', icon: 'Lock' },
  ],

  // Why Choose Us
  why_choose_us: [
    { title: 'Experienced Faculty', description: 'Highly qualified educators dedicated to student growth and mentoring.', icon: 'Award' },
    { title: 'Student-Centred Learning', description: 'Customized attention suited for diverse learning paces and interests.', icon: 'UserCheck' },
    { title: 'Modern Infrastructure', description: 'Contemporary architecture with air-conditioned halls and digital amenities.', icon: 'Building' },
    { title: 'Strong Academic Foundation', description: 'Consistent top board examination results and national competition medals.', icon: 'GraduationCap' },
    { title: 'Sports & Extracurricular Activities', description: 'Over 20 clubs spanning debate, music, dance, robotics, and athletics.', icon: 'Sparkles' },
    { title: 'Safe Learning Environment', description: 'Strict anti-bullying policies, mental health guidance, and infirmary support.', icon: 'HeartHandshake' },
  ],

  // Statistics
  statistics: [
    { label: 'Years of Excellence', value: '25+' },
    { label: 'Students Enrolled', value: '2,500+' },
    { label: 'Faculty Members', value: '120+' },
    { label: 'Board Results', value: '98%' },
  ],

  // Gallery
  gallery: [
    { id: 1, title: 'Annual Science Fair', category: 'Events', image: 'https://images.unsplash.com/photo-1564069114553-74154c42325b?auto=format&fit=crop&w=800&q=80' },
    { id: 2, title: 'Modern Robotics Lab', category: 'Infrastructure', image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=800&q=80' },
    { id: 3, title: 'Sports Day Tournament', category: 'Sports', image: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=800&q=80' },
    { id: 4, title: 'Central Library', category: 'Infrastructure', image: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=800&q=80' },
    { id: 5, title: 'Music & Cultural Fest', category: 'Events', image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=800&q=80' },
    { id: 6, title: 'Primary Smart Classroom', category: 'Academics', image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80' },
  ],

  // Video Gallery
  video_gallery: [
    {
      id: 'v1',
      title: 'Hera Public School Campus Virtual Tour',
      thumbnail: 'https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?auto=format&fit=crop&w=800&q=80',
      duration: '3:45',
      youtube_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    },
    {
      id: 'v2',
      title: 'Annual Sports & Cultural Extravaganza',
      thumbnail: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=800&q=80',
      duration: '4:20',
      youtube_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    },
  ],

  // Latest Events
  events: [
    {
      id: 'e1',
      title: 'Annual Sports & Athletics Meet 2026',
      date: 'March 15, 2026',
      time: '09:00 AM - 04:00 PM',
      location: 'School Main Ground',
      description: 'Track events, inter-house championships, and parent relay races.',
      image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=600&q=80',
    },
    {
      id: 'e2',
      title: 'National STEM & Innovation Exhibition',
      date: 'April 02, 2026',
      time: '10:00 AM - 03:00 PM',
      location: 'Auditorium & Labs',
      description: 'Showcasing student-designed AI models, eco-projects, and working prototypes.',
      image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=600&q=80',
    },
    {
      id: 'e3',
      title: 'Parent-Teacher Conference (Term 2)',
      date: 'April 20, 2026',
      time: '08:30 AM - 01:30 PM',
      location: 'Respective Classrooms',
      description: 'Comprehensive evaluation discussion on student academic and personal growth.',
      image: 'https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=600&q=80',
    },
  ],

  // Latest News
  news: [
    {
      id: 'n1',
      title: 'Admissions Open for Academic Session 2026-27',
      date: 'February 10, 2026',
      category: 'Admissions',
      summary: 'Applications are now open for Nursery to Grade 9 & Grade 11. Apply online or visit campus.',
    },
    {
      id: 'n2',
      title: '100% Pass Percentage Recorded in CBSE Class 10 & 12 Boards',
      date: 'January 28, 2026',
      category: 'Achievement',
      summary: 'Over 45 students secured above 95% aggregate marks with regional rank holders in Science stream.',
    },
    {
      id: 'n3',
      title: 'New Solar Powered Green Energy Wing Inaugurated',
      date: 'January 15, 2026',
      category: 'Campus Update',
      summary: 'Hera Public School becomes 100% green energy powered with 150 kW rooftop solar plant.',
    },
  ],

  // Testimonials
  testimonials: [
    {
      id: 't1',
      quote:
        'Hera Public School has provided my child with unmatched confidence and strong core values. The teachers truly care about individual student development.',
      author: 'Rajesh & Sunita Verma',
      relation: 'Parents of Aarav (Grade 8)',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
    },
    {
      id: 't2',
      quote:
        'The STEM lab facilities and competitive exam guidance helped me clear JEE Advanced in my very first attempt. Grateful to the supportive faculty!',
      author: 'Priya Sharma',
      relation: 'Alumna (Batch of 2024)',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80',
    },
    {
      id: 't3',
      quote:
        'The balance between sports, academics, and debate clubs makes learning fun and engaging every single day.',
      author: 'Kabir Mehta',
      relation: 'Student (Grade 11, Science)',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80',
    },
  ],

  // Faculty Preview
  faculty: [
    { name: 'Dr. Ananya Sharma', designation: 'Principal', qualification: 'Ph.D. in Education, M.Sc. Physics' },
    { name: 'Prof. Vikramaditya Singh', designation: 'Vice Principal & Head of Science', qualification: 'M.Sc. Chemistry, B.Ed.' },
    { name: 'Mrs. Radhika Gupta', designation: 'Head of Mathematics', qualification: 'M.Sc. Mathematics, 15+ Yrs Exp.' },
    { name: 'Mr. David Miller', designation: 'Head of Sports & Athletics', qualification: 'M.P.Ed, Certified Athletic Coach' },
  ],

  // Admission Process
  admission_process: [
    { step: '01', title: 'Submit Application', description: 'Fill out the online admission form or pick up the prospectus from campus.' },
    { step: '02', title: 'Interaction / Test', description: 'Age-appropriate assessment and friendly interaction with student & parents.' },
    { step: '03', title: 'Document Verification', description: 'Verify birth certificate, previous report cards, and transfer certificate.' },
    { step: '04', title: 'Enrollment & Fee', description: 'Pay the admission fee to confirm seat allocation and receive uniform & book set.' },
  ],

  // CTA
  cta: {
    title: 'Admissions Open for 2026-27',
    subtitle: 'Give your child the foundation they need for a bright future.',
    button_text: 'Apply Online Now',
  },

  // Contact Information
  contact: {
    email: 'info@herapublicschool.edu',
    phone: '+91 98765 43210',
    address: 'Sector 50, Noida, Uttar Pradesh 201301',
    google_map_embed:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3504.686523992226!2d77.3789453!3d28.5492124!2m2!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce62955555555%3A0x123456789abcdef!2sSector%2050%2C%20Noida%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin',
  },

  // Social Links
  social_media: {
    facebook: 'https://facebook.com',
    twitter: 'https://twitter.com',
    instagram: 'https://instagram.com',
    youtube: 'https://youtube.com',
  },
};
